import _ from "lodash";

import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { connect } from "react-redux";

import ACLStore from "lib/aclStore";
import { Sentry } from "services/sentry";
import {
  generateRandomId,
  getFileType,
  getFileName,
  getImageDimensions,
  getMaxSize,
  getFileTypesAndMaxSizes,
} from "Utils";
import { BLOCKED_FILE_MIME_TYPES } from "Constants";
import {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  exceededSizeError,
  heicToJpg,
  unsupportedFileExtensionError,
} from "modules/Services";
import {
  checkUserGooglePhotoAccess,
  getFileExtensionFromFilename,
  getFileExtensionFromMimeType,
} from "Utils/AttachmentHelpers";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import ThirdPartyAuth from "AppComponents/ThirdPartyAuth";
import FullScreenLoader from "UIComponents/FullScreenLoader/FullScreenLoader";
import DrivePermissionModal from "UIComponents/GooglePicker/DrivePermissionModal";

import {
  createPickerSession,
  deletePickerSession,
  checkMediaItemsReady,
  listSelectedMediaItems,
  getFileSize,
} from "./modules/GooglePhotosPickerModule";

const GooglePhotosPicker = forwardRef(
  (
    {
      renderComponent,
      parentId,
      parentType,
      downloadFromUrl,
      updateFilesCount,
      onContentUploaded,
      onSingleContentUploaded,
      onMultipleFilesUploaded,
      onContentLoaded,
      shouldUpload = true,
      shouldReturnSignedUrl,
      portalType,
      removeProgressElement,
      filePickSizeLimit,
      unsupportedFileExtensionError,
      exceededSizeError,
      heicToJpg,
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(false);
    const [accountId, setAccountId] = useState("");
    const [showDrivePermissionModal, setShowDrivePermissionModal] = useState(
      false
    );

    const pollingTimeoutRef = useRef(null);
    const pickerTimeoutRef = useRef(null);
    const pickerSessionRef = useRef(null);
    const authPopUpRef = useRef(null);

    const onAuthClick = () => {
      setIsLoading(true);
      const authInstance = _.get(authPopUpRef, "current.refs.wrappedInstance");
      if (authInstance?.onAuthClick) {
        authInstance.onAuthClick();
      }
    };

    const clearTimeouts = () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      if (pickerTimeoutRef.current) {
        clearTimeout(pickerTimeoutRef.current);
        pickerTimeoutRef.current = null;
      }
    };
    const closePickerSession = () => {
      setIsLoading(false);
      clearTimeouts();
      const pickerSession = pickerSessionRef.current;
      if (pickerSession) {
        deletePickerSession({
          accessToken: pickerSession?.accessToken,
          id: pickerSession?.id,
        });
      }
      pickerSessionRef.current = null;
    };
    const openPickerWindow = () => {
      const pickerSession = pickerSessionRef.current;
      if (pickerSession?.pickerUrl) {
        const width = 800;
        const height = 600;
        const left = (window.innerWidth - width) / 2 + window.screenX;
        const top = (window.innerHeight - height) / 2 + window.screenY;

        // eslint-disable-next-line no-restricted-properties
        window.open(
          `${pickerSession.pickerUrl}/autoclose`,
          "_blank",
          `width=${width},height=${height},top=${top},left=${left}`
        );
      }
    };

    useEffect(() => {
      return () => {
        closePickerSession();
      };
    }, []);

    useImperativeHandle(ref, () => {
      return {
        launchGooglePhotosPicker() {
          if (pickerSessionRef.current?.pickerUrl) {
            openPickerWindow();
          } else {
            onAuthClick();
          }
        },
      };
    });

    const handleOnMultipleFileUpload = async sendObjList => {
      if (onMultipleFilesUploaded) {
        const values = _.filter(
          await Promise.all(sendObjList),
          urlObj => urlObj
        );
        onMultipleFilesUploaded(values);
      }
    };
    const onFileDownloadedFromDrive = async ({
      file,
      uploadId,
      startProgress,
    }) => {
      let updatedFile = file;
      if (
        ACLStore.can("FeatureFlag:EnableHEICConversion") &&
        _.includes(["image/heic", "image/heif"], _.get(file, "type"))
      ) {
        try {
          updatedFile = await heicToJpg({ file, uploadId, startProgress: 0 });
        } catch (e) {
          Sentry.captureMessage("HEIC_CONVERSION_ISSUE_", {
            extra: {
              functionName: "heicToJpg",
              reason: _.get(e, "message"),
              fileType: _.get(file, "type"),
              component: "GooglePhotosPicker",
            },
          });
        }
      }
      if (onContentLoaded) {
        //handle your own upload
        const url = URL.createObjectURL(updatedFile);
        updatedFile["downloadUrl"] = url;

        const sendObj = await getSendObject(updatedFile);
        onContentLoaded({
          ...sendObj,
          file: updatedFile,
          url,
        });
        removeProgressElement({ id: uploadId });
        updateFilesCount(-1);
      } else {
        if (shouldUpload) {
          return new Promise((resolve, reject) => {
            getSendObject(updatedFile)
              .then(sendObj => {
                const uploadStartTime = Date.now();
                const { promise } = uploadFile({
                  file: updatedFile,
                  uploadId,
                  startProgress,
                  parentType,
                  parentId,
                  attachment: sendObj,
                  portalType,
                  shouldReturnSignedUrl,
                });
                promise
                  .then(async item => {
                    let sendObj = await getSendObject({
                      ...updatedFile,
                      type: updatedFile.type,
                      size: updatedFile.size,
                      downloadUrl: item,
                    });
                    const uploadTime = Date.now() - uploadStartTime;
                    const metadata = _.get(sendObj, "metadata", {});
                    const updatedMetadata = { ...metadata, uploadTime };
                    sendObj = { ...sendObj, metadata: updatedMetadata };
                    if (onContentUploaded) {
                      await onContentUploaded(sendObj);
                    }
                    if (onSingleContentUploaded) {
                      await onSingleContentUploaded(sendObj, uploadId, {
                        parentId,
                        parentType,
                      });
                    }
                    removeProgressElement({ id: uploadId });
                    updateFilesCount(-1);
                    resolve(sendObj);
                  })
                  .catch(e => {
                    console.error(e);
                    updateFilesCount(-1, true);
                  });
              })
              .catch(reject);
          });
        }
      }
    };

    const getSendObject = async file => {
      const mimeType = file.mimeType ? file.mimeType : file.type;
      const url = file.url ? file.url : file.downloadUrl;
      const attachmentType = getFileType(mimeType).text;
      const dimensions = file.dimensions
        ? file.dimensions
        : await getImageDimensions({
            url,
            type: attachmentType,
          });
      const metadata = {
        size: _.get(file, "size"),
        serviceType: "GOOGLE",
        isFromGooglePhotos: true,
        fileId: _.get(file, "id"),
        ...dimensions,
      };

      return {
        type: attachmentType,
        url: url,
        name: getFileName(file.name, mimeType),
        mimeType: mimeType,
        metadata: metadata,
      };
    };
    const fileDownloadAndUpload = async ({ item, sendObjList, fileCount }) => {
      const sendObj = await getSendObject(item);
      const fileUploadId = generateRandomId();
      const startProgress = 50;
      updateFilesCount(1);
      const pickerSession = pickerSessionRef.current;
      const accessToken = pickerSession?.accessToken;

      downloadFromUrl({
        downloadUrl: _.get(sendObj, "url"),
        size: _.get(sendObj, "metadata.size", 0),
        name: _.get(sendObj, "name", "Untitled"),
        fileUploadId: fileUploadId,
        parentType: parentType,
        parentId: parentId,
        attachment: sendObj,
        headers: [{ label: "Authorization", value: `Bearer ${accessToken}` }],
        onContentLoadFailed: async () => {
          updateFilesCount(-1, true);
          sendObjList.push(null);
          if (_.size(sendObjList) === fileCount) {
            await Promise.all(sendObjList);
            closePickerSession();
          }
        },
        onLoaded: async fileBlob => {
          if (fileBlob) {
            fileBlob.name = sendObj.name;
            if (!fileBlob.type) {
              fileBlob.mimeType = sendObj.mimeType;
            }
          }
          const downloadedObj = onFileDownloadedFromDrive({
            file: fileBlob,
            uploadId: fileUploadId,
            startProgress: startProgress,
          });
          sendObjList.push(downloadedObj);

          if (_.size(sendObjList) === fileCount) {
            handleOnMultipleFileUpload(sendObjList);
          }
        },
      });
    };

    // ================== Fetch Media ==================
    const onSelectedItemsReady = async () => {
      try {
        const pickerSession = pickerSessionRef.current;
        const { accessToken, id } = pickerSession;
        let mediaItems = await listSelectedMediaItems({
          accessToken,
          id,
        });
        const totalFiles = mediaItems.length;
        let unSupportedFormatFiles = 0;
        let filesExceedingSizeLimit = 0;

        const notAllowedFileExtensions = [];
        mediaItems = _.filter(mediaItems, item => {
          const { type } = item;
          const mediaFile = _.get(item, "mediaFile", {});
          const { mimeType, filename } = mediaFile;
          if (
            type !== "PHOTO" ||
            _.includes(BLOCKED_FILE_MIME_TYPES, mimeType)
          ) {
            unSupportedFormatFiles++;

            const extension = `.${
              getFileExtensionFromFilename({
                fileName: filename,
              }) || getFileExtensionFromMimeType({ mimeType: mimeType })
            }`;
            notAllowedFileExtensions.push(extension);
            return false;
          }
          return true;
        });

        if (unSupportedFormatFiles > 0) {
          unsupportedFileExtensionError({
            notAllowedFileExtensions: _.uniq(notAllowedFileExtensions),
            notAllowedFileCount: _.size(notAllowedFileExtensions),
          });
        }
        const mappedItems = await Promise.all(
          _.map(mediaItems, async item => {
            const { id, type } = item;
            const mediaFile = _.get(item, "mediaFile", {});
            const mediaFileMetadata = _.get(mediaFile, "mediaFileMetadata", {});
            const { mimeType, filename } = mediaFile;
            const downloadUrl = _.get(mediaFile, "baseUrl", "") + "=d"; // append =d for download URL
            const size = await getFileSize({ downloadUrl, accessToken });
            return {
              id,
              type,
              downloadUrl,
              mimeType,
              name: filename,
              size,
              dimensions: {
                width: mediaFileMetadata?.width,
                height: mediaFileMetadata?.height,
              },
            };
          })
        );

        const filteredItems = _.filter(mappedItems, item => {
          if (item?.size > filePickSizeLimit) {
            filesExceedingSizeLimit++;
            return false;
          }
          return true;
        });

        if (filesExceedingSizeLimit > 0) {
          const fileTypesAndMaxSizes = getFileTypesAndMaxSizes({
            files: mappedItems,
            allowedFiles: filteredItems,
            fileTypePropName: "mimeType",
            maxSize: filePickSizeLimit,
            maxSizeForVideoFiles: filePickSizeLimit,
            maxSizeForZipFiles: filePickSizeLimit,
          });
          setTimeout(() => {
            exceededSizeError({
              fileTypesAndMaxSizes,
            });
          });
        }
        setIsLoading(false);
        if (filesExceedingSizeLimit + unSupportedFormatFiles === totalFiles) {
          closePickerSession();
          return;
        }
        const sendObjList = [];
        _.forEach(filteredItems, async item => {
          fileDownloadAndUpload({
            item,
            sendObjList,
            fileCount: filteredItems.length,
          });
        });
      } catch (err) {
        console.error("Error processing selected media items:", err);
        setToastMsg("toastMsgs:something_went_wrong");
        closePickerSession();
      }
    };

    const handleAuthSuccess = async ({ accessToken, id }) => {
      const hasSufficientDriveAccess = await checkUserGooglePhotoAccess({
        accessToken,
      });
      if (!hasSufficientDriveAccess) {
        setIsLoading(false);
        setShowDrivePermissionModal(true);
        setAccountId(id);
        return;
      }
      setIsLoading(true);
      const pickerSession = await createPickerSession({ accessToken });
      if (!pickerSession) {
        setIsLoading(false);
        setToastMsg("toastMsgs:something_went_wrong");
        closePickerSession();
        return;
      }
      pickerSessionRef.current = {
        accessToken,
        id: pickerSession.id,
        pickerUrl: pickerSession.pickerUri,
      };
      //here set ref for picker session and also set polling session and picker timeout
      openPickerWindow();
      setIsLoading(false);
      //after opening window start polling and picker timeout
      const pollPicker = async () => {
        const isReady = await checkMediaItemsReady({
          accessToken,
          id: pickerSession.id,
        });
        if (isReady) {
          setIsLoading(true);
          clearTimeouts();
          await onSelectedItemsReady();
        } else {
          pollingTimeoutRef.current = setTimeout(pollPicker, 5000);
        }
      };
      pollingTimeoutRef.current = setTimeout(pollPicker, 5000);

      pickerTimeoutRef.current = setTimeout(() => {
        closePickerSession();
      }, 15 * 60 * 1000);
    };
    const handleAuthFailure = () => {
      setToastMsg("toastMsgs:something_went_wrong");
      closePickerSession();
    };
    const closeDrivePermissionModal = () => {
      setShowDrivePermissionModal(false);
      setAccountId("");
    };
    const handleStopPropagation = e => {
      e?.stopPropagation?.();
    };
    return (
      <>
        <ThirdPartyAuth
          serviceType={"GOOGLE"}
          ref={authPopUpRef}
          onSuccessCallBack={handleAuthSuccess}
          onFailureCallBack={handleAuthFailure}
        />
        {renderComponent}
        {isLoading && <FullScreenLoader />}
        {showDrivePermissionModal && !_.isEmpty(accountId) && (
          <div onClick={handleStopPropagation}>
            <DrivePermissionModal
              accountId={accountId}
              onAuthClick={onAuthClick}
              closeModal={closeDrivePermissionModal}
              isPhotosPicker={true}
            />
          </div>
        )}
      </>
    );
  }
);
GooglePhotosPicker.displayName = "GooglePhotosPicker";

const mapStateToProps = (state, ownProps) => {
  let { maxSize } = ownProps;
  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
  const allowInfiniteFileSize = ACLStore.can(
    "FeatureFlag:DropzoneInfiniteFileSize"
  );
  maxSize = getMaxSize({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
  });
  return {
    filePickSizeLimit: maxSize,
  };
};
const mapActionCreators = {
  uploadFile,
  setProgress,
  downloadFromUrl,
  removeProgressElement,
  setToastMsg,
  exceededSizeError,
  heicToJpg,
  unsupportedFileExtensionError,
};
export default connect(mapStateToProps, mapActionCreators, null, {
  withRef: true,
})(GooglePhotosPicker);
